import React, { useState, useEffect, useRef } from 'react';
import './TalentMatrix.css';
import EmployeeForm from './EmployeeForm';
import TalentStats from './TalentStats';
import * as XLSX from 'xlsx';
import PrintReport from './PrintReport';
import TrendAnalysis from './TrendAnalysis';
import TeamCapability from './TeamCapability';
import DataAnalysis from './DataAnalysis';
import DataService from '../services/DataService';

const TalentMatrix = () => {
  const [employees, setEmployees] = useState(() => {
    // 从本地文件读取数据
    const data = DataService.loadData();
    return data.employees || [];
  });
  const [showForm, setShowForm] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    categoryCount: {}
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const fileInputRef = useRef(null);

  // 定义九宫格矩阵类别 - 按图片布局排列
  const matrixCategories = [
    // 第一行 (潜力高)
    [
      { 
        name: '高潜人才', 
        description: '潜力高,绩效低,需要重点培养',
        potential: '高',
        performance: '低'
      },
      { 
        name: '关键人才', 
        description: '潜力高,绩效中,未来骨干',
        potential: '高',
        performance: '中'
      },
      { 
        name: '明星人才', 
        description: '潜力高,绩效高,重点保留',
        potential: '高',
        performance: '高'
      }
    ],
    // 第二行 (潜力中)
    [
      { 
        name: '待提升人才', 
        description: '潜力中,绩效低,需要辅导',
        potential: '中',
        performance: '低'
      },
      { 
        name: '骨干人才',
        description: '潜力中,绩效中,稳定中坚',
        potential: '中',
        performance: '中'
      },
      { 
        name: '核心人才', 
        description: '潜力中,绩效高,中流砥柱',
        potential: '中',
        performance: '高'
      }
    ],
    // 第三行 (潜力低)
    [
      { 
        name: '低绩效人才', 
        description: '潜力低,绩效低,需要优化',
        potential: '低',
        performance: '低',
        isWarning: true
      },
      { 
        name: '一般人才', 
        description: '潜力低,绩效中,可以胜任',
        potential: '低',
        performance: '中'
      },
      { 
        name: '需观察人才', 
        description: '潜力低,绩效高,有待提升',
        potential: '低',
        performance: '高'
      }
    ]
  ];

  const [showStats, setShowStats] = useState(false);
  const [showPrintReport, setShowPrintReport] = useState(false);
  const [showTrends, setShowTrends] = useState(false);
  const [showCapability, setShowCapability] = useState(false);

  // 保存数据到本地文件
  useEffect(() => {
    DataService.saveData({ employees });
    updateStats();
  }, [employees]);

  // 更新统计数据
  const updateStats = () => {
    const categoryCount = {};
    matrixCategories.flat().forEach(category => {
      categoryCount[category.name] = employees.filter(emp => emp.category === category.name).length;
    });

    setStats({
      total: employees.length,
      categoryCount
    });
  };

  const handleAddEmployee = () => {
    setShowForm(true);
    setCurrentEmployee(null);
  };

  const handleSaveEmployee = (employeeData) => {
    if (currentEmployee) {
      setEmployees(employees.map(emp => 
        emp.id === employeeData.id ? employeeData : emp
      ));
    } else {
      setEmployees([...employees, employeeData]);
    }
  };

  const handleDeleteEmployee = (employeeId) => {
    if (window.confirm('确定要删除这名员工吗？')) {
      setEmployees(employees.filter(emp => emp.id !== employeeId));
    }
  };

  // 过滤员工列表
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.name.includes(searchTerm) || 
                         emp.position.includes(searchTerm) ||
                         emp.skills.includes(searchTerm);
    const matchesCategory = filterCategory === 'all' || emp.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // 处理文件导入
  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      if (file.name.endsWith('.json')) {
        // 处理 JSON 导入
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = JSON.parse(e.target.result);
            if (Array.isArray(data)) {
              // 验证数据格式
              const isValid = data.every(item => 
                item.name && 
                item.position && 
                (!item.category || matrixCategories.flat().some(c => c.name === item.category))
              );

              if (!isValid) {
                alert('JSON数据格式不正确，请检查必填字段和类别值');
                return;
              }

              if (window.confirm(`确定要导入 ${data.length} 条数据吗？这将覆盖现有数据。`)) {
                // 添加ID和默认值
                const formattedData = data.map(item => ({
                  id: Date.now() + Math.random(),
                  category: '核心人才',
                  skills: '',
                  idpGoals: '',
                  idpProgress: '',
                  history: [],
                  ...item
                }));
                setEmployees(formattedData);
              }
            }
          } catch (error) {
            alert('JSON 格式错误：' + error.message);
          }
        };
        reader.readAsText(file);
      } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        // 处理 Excel 导入
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            
            if (jsonData.length > 0) {
              // 验证数据格式
              const isValid = jsonData.every(row => 
                row['姓名'] && 
                row['职位'] && 
                (!row['类别'] || matrixCategories.flat().some(c => c.name === row['类别']))
              );

              if (!isValid) {
                alert('Excel数据格式不正确，请检查必填字段和类别值');
                return;
              }

              if (window.confirm(`确定要导入 ${jsonData.length} 条数据吗？这将覆盖现有数据。`)) {
                const formattedData = jsonData.map(row => ({
                  id: Date.now() + Math.random(),
                  name: row['姓名'],
                  position: row['职位'],
                  category: row['类别'] || '核心人才',
                  skills: row['技能特长'] || '',
                  idpGoals: row['IDP目标'] || '',
                  idpProgress: row['IDP进展'] || '',
                  history: []
                }));
                setEmployees(formattedData);
              }
            }
          } catch (error) {
            alert('Excel 格式错误：' + error.message);
          }
        };
        reader.readAsArrayBuffer(file);
      }
    } catch (error) {
      alert('导入失败：' + error.message);
    }
    
    // 清除文件选择，允许重复选择同一文件
    event.target.value = '';
  };

  // 修改Excel导出功
  const handleExportExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // 创建九宫格分布表
    const matrixData = [
      ['九宫格分布统计'],
      ['类别', '人数', '占比', '潜力', '绩效', '说明', '人员名单'],
      ...matrixCategories.flat().map(category => {
        const categoryEmployees = employees.filter(emp => emp.category === category.name);
        return [
          category.name,
          categoryEmployees.length,
          `${((categoryEmployees.length / employees.length) * 100).toFixed(1)}%`,
          category.potential,
          category.performance,
          category.description,
          categoryEmployees.map(emp => emp.name).join(', ')
        ];
      })
    ];
    const matrixSheet = XLSX.utils.aoa_to_sheet(matrixData);
    XLSX.utils.book_append_sheet(workbook, matrixSheet, '九宫格分布');

    // 创建员工详细信息表
    const employeeData = [
      ['员工详细信息'],
      ['姓名', '职位', '所属类别', '潜力', '绩效', '技能特长', 'IDP目标', 'IDP进展', '最近变动', '变动原因', '历史记录'],
      ...employees.map(emp => {
        const latestHistory = emp.history && emp.history.length > 0 
          ? emp.history[emp.history.length - 1] 
          : null;
        const category = matrixCategories.flat().find(c => c.name === emp.category);
        const historyText = (emp.history || [])
          .map(h => `${new Date(h.date).toLocaleDateString()}: ${h.fromCategory} → ${h.toCategory}${h.reason ? ` (${h.reason})` : ''}`)
          .join('\n');
        
        return [
          emp.name,
          emp.position,
          emp.category,
          category?.potential || '',
          category?.performance || '',
          emp.skills,
          emp.idpGoals,
          emp.idpProgress,
          latestHistory ? `${latestHistory.fromCategory} → ${latestHistory.toCategory}` : '',
          latestHistory?.reason || '',
          historyText
        ];
      })
    ];
    const employeeSheet = XLSX.utils.aoa_to_sheet(employeeData);
    XLSX.utils.book_append_sheet(workbook, employeeSheet, '员工详情');

    // 创建技能分布表
    const skillsMap = {};
    employees.forEach(emp => {
      if (emp.skills) {
        const skills = emp.skills.split(',').map(s => s.trim());
        skills.forEach(skill => {
          if (!skillsMap[skill]) {
            skillsMap[skill] = {
              count: 0,
              employees: new Set(),
              categories: new Set()
            };
          }
          skillsMap[skill].count++;
          skillsMap[skill].employees.add(emp.name);
          skillsMap[skill].categories.add(emp.category);
        });
      }
    });

    const skillsData = [
      ['技能分布统计'],
      ['技能名称', '掌握人数', '占比', '掌握人员', '分布类别'],
      ...Object.entries(skillsMap)
        .sort(([, a], [, b]) => b.count - a.count)
        .map(([skill, data]) => [
          skill,
          data.count,
          `${((data.count / employees.length) * 100).toFixed(1)}%`,
          Array.from(data.employees).join(', '),
          Array.from(data.categories).join(', ')
        ])
    ];
    const skillsSheet = XLSX.utils.aoa_to_sheet(skillsData);
    XLSX.utils.book_append_sheet(workbook, skillsSheet, '技能分布');

    // 创建IDP统计表
    const idpData = [
      ['IDP目标统计'],
      ['姓名', '职位', '所属类别', 'IDP目标', 'IDP进展', '目标设定时间'],
      ...employees
        .filter(emp => emp.idpGoals || emp.idpProgress)
        .map(emp => [
          emp.name,
          emp.position,
          emp.category,
          emp.idpGoals,
          emp.idpProgress,
          emp.history && emp.history.length > 0 ? 
            new Date(emp.history[0].date).toLocaleDateString() : 
            '-'
        ])
    ];
    const idpSheet = XLSX.utils.aoa_to_sheet(idpData);
    XLSX.utils.book_append_sheet(workbook, idpSheet, 'IDP统计');

    // 导出Excel文件
    XLSX.writeFile(workbook, '人才盘点报告.xlsx');
  };

  // 添加导出模板功能
  const handleExportTemplate = () => {
    const workbook = XLSX.utils.book_new();
    
    // 创建模板表
    const templateData = [
      ['姓名', '职位', '类别', '技能特长', 'IDP目标', 'IDP进展'],
      ['张三', '工程师', '核心人才', 'Java,Spring Boot', '提升架构设计能力', '完成系统架构培训'],
      ['', '', '', '', '', ''] // 空行供填写
    ];
    const templateSheet = XLSX.utils.aoa_to_sheet(templateData);
    XLSX.utils.book_append_sheet(workbook, templateSheet, '导入模板');

    // 添加说明表
    const instructionsData = [
      ['字段', '说明', '是否必填'],
      ['姓名', '员工姓名', '是'],
      ['职位', '员工职位', '是'],
      ['类别', '人才类别（如：明星人才、核心人才等）', '否，默认为"核心人才"'],
      ['技能特长', '员工的技能和特长，多个技能用逗号分隔', '否'],
      ['IDP目标', '个人发展计划目标', '否'],
      ['IDP进展', '个人发展计划进展', '否']
    ];
    const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData);
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, '填写说明');

    // 导出Excel文件
    XLSX.writeFile(workbook, '人才盘点导入模板.xlsx');
  };

  // 修改单元格样式类
  const getCellClass = (category) => {
    return `matrix-cell potential-${category.potential} performance-${category.performance}`;
  };

  return (
    <div className="talent-matrix-container">
      {/* 工具栏 */}
      <div className="toolbar">
        <input
          type="text"
          className="search-input"
          placeholder="搜索..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="tool-button primary" onClick={handleAddEmployee}>
          <span className="icon">+</span>
          添加员工
        </button>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleImport}
          accept=".json,.xlsx,.xls"
        />
        <button 
          className="tool-button"
          onClick={() => fileInputRef.current.click()}
        >
          <span className="icon">📥</span>
          导入
        </button>
        <button 
          className="tool-button"
          onClick={handleExportTemplate}
        >
          <span className="icon">📋</span>
          模板
        </button>
        <button 
          className="tool-button"
          onClick={handleExportExcel}
        >
          <span className="icon">📤</span>
          导出
        </button>
        <button 
          className={`tool-button ${showStats ? 'active' : ''}`} 
          onClick={() => setShowStats(!showStats)}
        >
          <span className="icon">📊</span>
          统计
        </button>
        <button 
          className={`tool-button ${showCapability ? 'active' : ''}`} 
          onClick={() => setShowCapability(!showCapability)}
        >
          <span className="icon">🎯</span>
          能力
        </button>
        <button 
          className={`tool-button ${showTrends ? 'active' : ''}`} 
          onClick={() => setShowTrends(!showTrends)}
        >
          <span className="icon">📈</span>
          趋势
        </button>
        <button 
          className="tool-button"
          onClick={() => setShowPrintReport(true)}
        >
          <span className="icon">🖨️</span>
          打印
        </button>
      </div>

      <div className="matrix-wrapper">
        <div className="matrix-container">
          <div className="axis-labels">
            <div className="y-axis">
              <div className="axis-title">潜力</div>
              <div className="axis-values">
                <div className="axis-value">高</div>
                <div className="axis-value">中</div>
                <div className="axis-value">低</div>
              </div>
            </div>
            <div className="x-axis">
              <div className="axis-values">
                <div className="axis-value">低</div>
                <div className="axis-value">中</div>
                <div className="axis-value">高</div>
              </div>
              <div className="axis-title">绩效</div>
            </div>
          </div>
          
          <div className="matrix-grid">
            {[0, 1, 2].map((rowIndex) => (
              <div key={rowIndex} className="matrix-row">
                {[0, 1, 2].map((colIndex) => {
                  const category = matrixCategories[colIndex][rowIndex];
                  return (
                    <div 
                      key={`${rowIndex}-${colIndex}`} 
                      className={getCellClass(category)}
                    >
                      <div className="cell-header">
                        <h3>{category.name}</h3>
                        <div className="cell-description" style={category.isWarning ? { color: '#d32f2f' } : undefined}>
                          {category.description}
                        </div>
                        <div className="cell-metrics">
                          <div className={`metric-tag potential-${category.potential}`}>
                            潜力: {category.potential}
                          </div>
                          <div className={`metric-tag performance-${category.performance}`}>
                            绩效: {category.performance}
                          </div>
                        </div>
                        <div className="employee-count">
                          <span className="count-number">{stats.categoryCount[category.name] || 0}</span>
                          <span className="count-label">人</span>
                        </div>
                      </div>
                      <div className={`employee-list ${
                        filteredEmployees.filter(emp => emp.category === category.name).length > 16 
                          ? 'scrollable' 
                          : ''
                      }`}>
                        {filteredEmployees
                          .filter(emp => emp.category === category.name)
                          .map(emp => (
                            <div 
                              key={emp.id} 
                              className="employee-card"
                              onClick={() => {
                                setCurrentEmployee(emp);
                                setShowForm(true);
                              }}
                            >
                              <div className="employee-info">
                                <div className="employee-name">{emp.name}</div>
                                <div className="employee-position">{emp.position}</div>
                              </div>
                              <button 
                                className="delete-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteEmployee(emp.id);
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showStats && (
        <DataAnalysis 
          employees={employees}
          matrixCategories={matrixCategories}
        />
      )}

      {showForm && (
        <EmployeeForm
          employee={currentEmployee}
          onSave={handleSaveEmployee}
          onClose={() => setShowForm(false)}
          matrixCategories={matrixCategories}
        />
      )}

      {showPrintReport && (
        <PrintReport
          employees={employees}
          matrixCategories={matrixCategories}
          onClose={() => setShowPrintReport(false)}
        />
      )}

      {showTrends && (
        <TrendAnalysis 
          employees={employees}
          matrixCategories={matrixCategories}
        />
      )}

      {showCapability && (
        <TeamCapability employees={employees} />
      )}
    </div>
  );
};

export default TalentMatrix; 